/**
 * Data plane — Dataset Relationships (L2 knowledge graph) API client (ADR-0026 §4).
 *
 * Mirrors `backend/src/hub/routes/relationships.py`. A relationship is a
 * directed edge `from_dataset.from_field → to_dataset.to_field` with a
 * cardinality. The `/graph` endpoint returns ready-to-render nodes + edges.
 */
import { api } from "./api";

export type Cardinality = "one_to_one" | "one_to_many" | "many_to_one" | "many_to_many";

export interface Relationship {
  id: string;
  name: string;
  description: string | null;
  from_dataset_id: string;
  from_dataset_name: string;
  from_field: string;
  to_dataset_id: string;
  to_dataset_name: string;
  to_field: string;
  cardinality: Cardinality;
  config: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface RelationshipCreate {
  name: string;
  description?: string | null;
  from_dataset_id: string;
  from_field: string;
  to_dataset_id: string;
  to_field: string;
  cardinality: Cardinality;
  config?: Record<string, unknown>;
}

export type RelationshipUpdate = Partial<{
  name: string;
  description: string | null;
  from_field: string;
  to_field: string;
  cardinality: Cardinality;
  config: Record<string, unknown>;
}>;

export interface GraphNode {
  id: string;
  name: string;
  binding: "internal" | "external";
  /** Owning source name for external datasets (null for internal). */
  source?: string | null;
}

export interface GraphEdge {
  id: string;
  name: string;
  from_dataset_id: string;
  from_field: string;
  to_dataset_id: string;
  to_field: string;
  cardinality: Cardinality;
}

export interface RelationshipGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

/** A proposed relationship from deterministic discovery (no AI). */
export interface RelationshipCandidate {
  name: string;
  from_dataset_id: string;
  from_dataset_name: string;
  from_field: string;
  to_dataset_id: string;
  to_dataset_name: string;
  to_field: string;
  cardinality: Cardinality;
  /** `fk` / `app_relation` / `value_overlap` (exact) | `heuristic` (name only) |
   *  `ai` (meaning-inferred, streamed from `/propose-ai`). */
  source: "fk" | "app_relation" | "value_overlap" | "heuristic" | "ai";
}

/** An AI-proposed candidate carries its confidence + the model's rationale, on
 *  top of the deterministic candidate shape (`source: "ai"`). */
export interface AiRelationshipCandidate extends RelationshipCandidate {
  source: "ai";
  confidence: "high" | "medium" | "low";
  rationale: string;
}

/** Compact cardinality glyph for edge labels (N:1, 1:N, 1:1, N:N). */
export const CARDINALITY_GLYPH: Record<Cardinality, string> = {
  one_to_one: "1:1",
  one_to_many: "1:N",
  many_to_one: "N:1",
  many_to_many: "N:N",
};

export const relationshipsApi = {
  vocab: () => api.get<{ cardinalities: string[] }>("/relationships/vocab"),

  graph: () => api.get<RelationshipGraph>("/relationships/graph"),

  /** Deterministic discovery (no AI) — FK + app relations + name heuristic. */
  discover: () =>
    api.post<{ candidates: RelationshipCandidate[] }>("/relationships/discover", {}),

  list: (params?: { dataset_id?: string }) =>
    api.get<Relationship[]>("/relationships", { query: params }),

  get: (id: string) => api.get<Relationship>(`/relationships/${id}`),

  create: (body: RelationshipCreate) => api.post<Relationship>("/relationships", body),

  update: (id: string, body: RelationshipUpdate) =>
    api.patch<Relationship>(`/relationships/${id}`, body),

  remove: (id: string) => api.delete<void>(`/relationships/${id}`),
};
